/**
 * `/checkout/success` — plan 030 post-Checkout landing.
 *
 * Stripe redirects here after a successful checkout. The Stripe webhook
 * on the CP will flip `families.subscription_status` to `trialing` (or
 * `active` if no trial), but that's asynchronous — race the poll against
 * the redirect and don't drop the parent onto a stale "not subscribed"
 * dashboard.
 *
 * Poll `/v1/family/billing/status` for up to 8 seconds. When we see a
 * trialing/active transition (or entitled=true), route to dashboard with
 * a success flash. If the poll runs out, still route to dashboard — the
 * BillingBanner will pick up the state once the webhook lands.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Card } from '../components/Card';
import { getBillingStatus } from '../data/billing';
import { clearCheckoutIntent } from '../state/checkout-intent';
import { setFlash } from '../state/flash';

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 8_000;

export function CheckoutSuccessRoute() {
  const location = useLocation();
  const [note, setNote] = useState('Confirming your subscription…');

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    async function poll(): Promise<void> {
      while (!cancelled) {
        try {
          const status = await getBillingStatus();
          if (
            status.entitled ||
            status.subscription_status === 'trialing' ||
            status.subscription_status === 'active'
          ) {
            setFlash({
              kind: 'checkout-ok',
              message:
                'Card added — your 14-day trial is running. You can manage billing any time in Settings.',
            });
            clearCheckoutIntent();
            location.route('/dashboard');
            return;
          }
        } catch {
          // transient — keep trying.
        }
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          setNote(
            "Payment received. Your account is updating — we'll land you on the dashboard now.",
          );
          clearCheckoutIntent();
          window.setTimeout(() => {
            location.route('/dashboard');
          }, 1500);
          return;
        }
        await new Promise((r) => window.setTimeout(r, POLL_INTERVAL_MS));
      }
    }

    void poll();
    return () => {
      cancelled = true;
    };
  }, [location]);

  return (
    <main class="flex min-h-screen items-center justify-center p-4">
      <Card class="w-full max-w-md space-y-3">
        <h1 class="text-xl font-semibold">Thanks for subscribing.</h1>
        <p class="text-sm text-surface-fg/70">{note}</p>
      </Card>
    </main>
  );
}
